import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useMemo, useReducer, useState } from 'react';

import ndcsMatch from '../utils/ndcsMatch';
import { loadStoredHistory, saveStoredHistory } from '../utils/sessionHistoryStorage';

const ScanSessionContext = createContext(null);

const newId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
  `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const initialState = {
  current: null, // { id, scanId, rx: ParsedLabel, vials: [{id, scanId, fields}], startedAt }
  history: [], // committed sessions, newest first
  pendingVial: null, // vial scanned before any RX bottle
};

const initState = () => ({ ...initialState, history: loadStoredHistory() });

export const hasValue = (v) => typeof v === 'string' && v.trim().length > 0;

// Vial quantity may arrive as a number (from the scan app or the default) or
// a string (typed by the operator). Must be a positive whole number — you
// can't dispense a fraction of a vial.
const isPositiveInteger = (v) => {
  if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) return false;
  const n = Number(v);
  return Number.isInteger(n) && n > 0;
};

/** Resolve the productId that ships with the Rx's prescribed NDC, looked up
 *  from the live inventory listing (not from anything the vial itself
 *  reports). The listing only holds received lots, so a drug never received
 *  falls back to the productId on the Rx's own POM record. The listing wins
 *  when both exist, so a drug with lots under the Rx's NDC resolves as
 *  before. Null when neither knows the product. */
export const resolvePrescribedProductId = (rx, inventory) => {
  const rxRecordProductId = hasValue(rx?.productId) ? rx.productId : null;
  const rxNdc = rx?.ndc;
  if (!hasValue(rxNdc)) return rxRecordProductId;
  const match = (inventory ?? []).find((row) => hasValue(row?.ndc) && ndcsMatch(row.ndc, rxNdc));
  return match?.productId || rxRecordProductId;
};

/** True when a vial's productId is known and differs from the Rx's
 *  prescribed productId. Vial productId missing → false (caught earlier by
 *  missingProductId in commitBlockReason). Prescribed productId missing →
 *  true; an unresolvable prescribed NDC must block commit rather than be
 *  treated as nothing to compare — see resolvePrescribedProductId. */
export const vialProductMismatchesRx = (prescribedProductId, vial) =>
  hasValue(vial?.fields?.productId) &&
  (!hasValue(prescribedProductId) || vial.fields.productId !== prescribedProductId);

/** True when a vial's NDC is known and differs from the Rx's prescribed NDC.
 *  Purely informational for commit-blocking (the same product can legitimately
 *  ship under more than one NDC — see vialProductMismatchesRx for the check
 *  that actually blocks), but the fulfillment call still needs to know: a
 *  committed session containing one of these is a substitution and must be
 *  recorded via overrideFulfillmentNdc instead of fulfillProductByRxNumber. */
export const vialNdcDiffersFromRx = (rx, vial) =>
  hasValue(vial?.fields?.ndc) && hasValue(rx?.ndc) && !ndcsMatch(vial.fields.ndc, rx.ndc);

/** True when any vial in the session is a same-product-different-NDC
 *  substitution (see vialNdcDiffersFromRx). Used to route a commit to
 *  overrideFulfillmentNdc instead of the ordinary fulfillment mutation. */
export const sessionHasNdcSubstitution = (session) =>
  (session?.vials ?? []).some((v) => vialNdcDiffersFromRx(session?.rx, v));

/** True when any vial was recorded by hand in AddDrugByLotDialog's
 *  unverified-lot path. Such a session is fulfilled through
 *  manageProductFulfillmentStatus rather than the scan mutation (see
 *  fulfillCommittedSession in rxScanValidation.js), which changes what the
 *  commit needs — see commitBlockReason. */
export const sessionHasHandRecordedVial = (session) =>
  (session?.vials ?? []).some((v) => v.fields?.recordedManually === true);

/** Returns a user-facing reason the session cannot be committed, or '' when
 *  it is committable. Rules: at least one vial; Rx must have a drug name and
 *  an Rx number; every vial must have a drug name, a lot #, an NDC, a
 *  product id AND a positive whole-number quantity — the fulfillment mutation
 *  (fulfillProductByRxNumber / overrideFulfillmentNdc) rejects any
 *  drugDispensed entry with a blank ndc, so this must fail fast in the UI
 *  rather than surface as a failed-fulfillment toast after commit. Every
 *  vial must also carry the SAME ndc as the others — the fulfillment
 *  mutation accepts only one ndc per call, so a session mixing a matching
 *  vial with a differently-substituted one can never be fulfilled in a
 *  single commit. The Rx's productId comes from resolvePrescribedProductId;
 *  if it can't be resolved commit is blocked outright, since an
 *  unverifiable product id must never be treated as "nothing to compare".
 *  Otherwise any vial whose productId doesn't match that blocks commit
 *  outright (no per-field NDC/name comparison, no confirm-to-override). Only
 *  once every vial matches is the dispensed quantity checked against the
 *  prescribed quantity. */
export const commitBlockReason = (session, inventory = []) => {
  if (!session) return 'No active session.';
  if (!hasValue(session.rx?.drugName)) return 'Rx bottle is missing a drug name.';
  if (!hasValue(session.rx?.rxNumber)) return 'Rx bottle is missing an Rx number.';
  if (session.vials.length === 0) return 'Scan at least one drug vial before committing.';
  const missingDrug = session.vials.some((v) => !hasValue(v.fields?.drugName));
  if (missingDrug) return 'All vials must have a drug name before committing.';
  const missingLot = session.vials.some((v) => !hasValue(v.fields?.lotNumber));
  if (missingLot) return 'All vials must have a lot # before committing.';
  const missingNdc = session.vials.some((v) => !hasValue(v.fields?.ndc));
  if (missingNdc) return 'All vials must have an NDC before committing.';
  const referenceNdc = session.vials[0].fields.ndc;
  const mixedNdc = session.vials.some((v) => !ndcsMatch(v.fields.ndc, referenceNdc));
  if (mixedNdc) {
    return 'All vials must share the same NDC before committing — a single commit can only fulfil one NDC.';
  }
  const missingProductId = session.vials.some((v) => !hasValue(v.fields?.productId));
  if (missingProductId) return 'All vials must have a product id before committing.';
  const missingQty = session.vials.some((v) => !isPositiveInteger(v.fields?.quantity));
  if (missingQty)
    return 'All vials must have a whole number quantity (greater than 0) before committing.';

  const prescribedProductId = resolvePrescribedProductId(session.rx, inventory);
  if (!hasValue(prescribedProductId)) {
    return "Could not verify product id — the Rx's prescribed NDC has no match in inventory.";
  }
  const productMismatch = session.vials.some((v) =>
    vialProductMismatchesRx(prescribedProductId, v)
  );
  if (productMismatch) {
    return 'A vial does not match the Rx — the product id does not match the prescribed NDC.';
  }

  // A session with a hand-recorded vial cannot go through the scan mutation
  // (only LotAllocationInput carries the manual-entry fields), so it is recorded
  // via manageProductFulfillmentStatus instead, which is keyed on
  // mpi + orderId + lineItemId. A same-product NDC substitution is fine on that
  // route: the mutation persists the dispensed NDC on the fill, re-syncs the
  // prescription override to it (POM-5826) and regenerates the label — the same
  // way Fill Product records a fill against a non-default NDC.
  if (
    sessionHasHandRecordedVial(session) &&
    (!hasValue(session.rx?.mpi) ||
      !hasValue(session.rx?.orderId) ||
      !hasValue(session.rx?.lineItemId))
  ) {
    return 'Cannot record a hand-entered lot for this Rx — its order details could not be resolved. Re-scan the Rx bottle.';
  }

  const rxQty = Number(session.rx?.quantity);
  if (Number.isFinite(rxQty) && rxQty > 0) {
    const dispensedQty = session.vials.reduce((sum, v) => sum + Number(v.fields?.quantity || 0), 0);
    if (dispensedQty !== rxQty) {
      return `Dispensed quantity (${dispensedQty}) does not match the prescribed quantity (${rxQty}).`;
    }
  }
  return '';
};

export const canCommitSession = (session, inventory) =>
  commitBlockReason(session, inventory) === '';

const reducer = (state, action) => {
  switch (action.type) {
    case 'rx-scanned': {
      const { parsed, scanId, timestamp, inventory } = action;
      // Duplicate RX scanned consecutively → ignore per spec.
      const currentRxNum = state.current?.rx?.rxNumber;
      if (currentRxNum && currentRxNum === parsed.rxNumber) {
        return state;
      }
      // If the current session can't be auto-committed (vials missing lot #),
      // keep it open and ignore the new RX scan. wireScanEvent surfaces the
      // toast so the user knows why nothing happened.
      if (state.current && !canCommitSession(state.current, inventory)) {
        return state;
      }
      const committed = state.current
        ? [{ ...state.current, committedAt: new Date().toISOString() }, ...state.history]
        : state.history;
      const next = {
        id: newId(),
        scanId,
        startedAt: timestamp,
        rx: { ...parsed },
        vials: state.pendingVial ? [state.pendingVial] : [],
      };
      return { current: next, history: committed, pendingVial: null };
    }
    case 'rx-validating': {
      if (!state.current || state.current.scanId !== action.scanId) return state;
      return {
        ...state,
        current: { ...state.current, rx: { ...state.current.rx, validating: true } },
      };
    }
    case 'rx-validated': {
      if (!state.current || state.current.scanId !== action.scanId) return state;
      // Authoritative POM record replaces the model-read fields it carries
      // (rxNumber/patient/drugName/strength/...). Fields absent from the record
      // (e.g. quantity) keep their scanned value.
      return {
        ...state,
        current: {
          ...state.current,
          rx: { ...state.current.rx, ...action.validated, validating: false, validated: true },
        },
      };
    }
    case 'rx-validation-failed': {
      // Discard the unvalidated scan so the operator re-scans. Guard by scanId
      // so a newer session started in the meantime is left untouched.
      if (!state.current || state.current.scanId !== action.scanId) return state;
      return { ...state, current: null };
    }
    case 'vial-scanned': {
      const { parsed, scanId, timestamp } = action;
      // Quantity defaults to 1 vial.
      const quantity = Number(parsed.quantity ?? 1) || 1;
      const newFields = { ...parsed, quantity };
      if (!state.current) {
        return {
          ...state,
          pendingVial: { id: newId(), scanId, scannedAt: timestamp, fields: newFields },
        };
      }
      // A rescan of the same lot # is the same physical batch, not a new one —
      // bump its quantity instead of adding a duplicate row. Same lot key used
      // by buildDrugDispensed (see rxScanValidation.js) when merging for fulfillment.
      const lotKey = hasValue(parsed.lotNumber) ? parsed.lotNumber.trim().toUpperCase() : null;
      const sameLot = (v) =>
        hasValue(v.fields?.lotNumber) && v.fields.lotNumber.trim().toUpperCase() === lotKey;
      const existingIdx = lotKey ? state.current.vials.findIndex(sameLot) : -1;
      if (existingIdx !== -1) {
        const existing = state.current.vials[existingIdx];
        const existingQty = Number(existing.fields?.quantity) || 0;
        const vials = [...state.current.vials];
        vials[existingIdx] = {
          ...existing,
          scanId,
          scannedAt: timestamp,
          fields: { ...existing.fields, quantity: existingQty + quantity },
        };
        return { ...state, current: { ...state.current, vials } };
      }
      const vial = { id: newId(), scanId, scannedAt: timestamp, fields: newFields };
      return {
        ...state,
        current: { ...state.current, vials: [...state.current.vials, vial] },
      };
    }
    case 'edit-rx': {
      if (!state.current) return state;
      return {
        ...state,
        current: { ...state.current, rx: { ...state.current.rx, ...action.patch } },
      };
    }
    case 'edit-vial': {
      if (!state.current) return state;
      return {
        ...state,
        current: {
          ...state.current,
          vials: state.current.vials.map((v) =>
            v.id === action.vialId ? { ...v, fields: { ...v.fields, ...action.patch } } : v
          ),
        },
      };
    }
    case 'remove-vial': {
      if (!state.current) return state;
      return {
        ...state,
        current: {
          ...state.current,
          vials: state.current.vials.filter((v) => v.id !== action.vialId),
        },
      };
    }
    case 'commit-current': {
      if (!state.current) return state;
      if (!canCommitSession(state.current, action.inventory)) return state;
      return {
        ...state,
        current: null,
        history: [{ ...state.current, committedAt: new Date().toISOString() }, ...state.history],
      };
    }
    case 'discard-current': {
      if (!state.current) return state;
      return { ...state, current: null };
    }
    case 'fulfill-result': {
      // Progress of the fulfillProductByRxNumber call for a committed session:
      // 'pending' | 'success' | 'failed' (+ fulfillError message when failed).
      return {
        ...state,
        history: state.history.map((s) =>
          s.id === action.sessionId
            ? { ...s, fulfillStatus: action.status, fulfillError: action.error }
            : s
        ),
      };
    }
    case 'label-status': {
      // Progress of the auto regenerate-and-print flow triggered when a
      // committed session's dispensed NDC differs from the Rx's prescribed
      // NDC: 'regenerating' → 'regenerated' | 'failed'. Absent entirely for
      // ordinary (non-substitution) sessions — see regenerateAndPrintLabel.
      return {
        ...state,
        history: state.history.map((s) =>
          s.id === action.sessionId ? { ...s, labelStatus: action.status } : s
        ),
      };
    }
    case 'print-status': {
      // Progress of sending the regenerated label to the printer via
      // JSPrintManager: 'printing' → 'printed' | 'failed'.
      return {
        ...state,
        history: state.history.map((s) =>
          s.id === action.sessionId ? { ...s, printStatus: action.status } : s
        ),
      };
    }
    case 'dismiss-pending-vial': {
      return { ...state, pendingVial: null };
    }
    case 'promote-pending-vial': {
      if (!state.current || !state.pendingVial) return state;
      return {
        ...state,
        current: { ...state.current, vials: [...state.current.vials, state.pendingVial] },
        pendingVial: null,
      };
    }
    case 'rollback-rx-swap': {
      // Optimistic-swap rollback: the RX bottle that opened `current` failed
      // POM validation, so restore the session it displaced. Guard by scanId so
      // a newer scan (which would have replaced `current`) is never clobbered.
      if (!state.current || state.current.scanId !== action.scanId) return state;
      const [restored, ...rest] = state.history;
      // No displaced session (this RX started fresh) → just drop the bad one.
      if (!restored) {
        return { ...state, current: null };
      }
      // Un-commit the previous session and re-parent any vials the operator
      // scanned into the provisional session during the validation window, so
      // those scans aren't lost when we roll back.
      return {
        ...state,
        current: {
          ...restored,
          committedAt: undefined,
          vials: [...restored.vials, ...state.current.vials],
        },
        history: rest,
      };
    }
    default:
      return state;
  }
};

export const ScanSessionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState, initState);
  // Re-fetched from the scan app on every (re)connect, so it lives outside the
  // reducer/localStorage-persisted state rather than as another action type.
  const [inventory, setInventory] = useState([]);

  useEffect(() => {
    saveStoredHistory(state.history);
  }, [state.history]);

  const value = useMemo(
    () => ({
      ...state,
      inventory,
      setInventory,
      // inventory is captured here (not read from state in the reducer) so the
      // auto-commit-block check below always sees the latest listing.
      onRxScanned: (parsed, scanId, timestamp) =>
        dispatch({ type: 'rx-scanned', parsed, scanId, timestamp, inventory }),
      markRxValidating: (scanId) => dispatch({ type: 'rx-validating', scanId }),
      applyRxValidation: (scanId, validated) =>
        dispatch({ type: 'rx-validated', scanId, validated }),
      failRxValidation: (scanId) => dispatch({ type: 'rx-validation-failed', scanId }),
      onVialScanned: (parsed, scanId, timestamp) =>
        dispatch({ type: 'vial-scanned', parsed, scanId, timestamp }),
      // Manual fallback for vials too large for the scanner rig: the matched
      // inventory row (see AddDrugByLotDialog) is added as a read-only vial.
      addVialFromInventory: (match) =>
        dispatch({
          type: 'vial-scanned',
          parsed: { ...match, manualEntry: true, fromInventory: true },
          scanId: null,
          timestamp: new Date().toISOString(),
        }),
      // Hand-recorded (unverified) vial: the lot is NOT in the scan app's
      // in-stock listing. Either the operator typed / picked it in
      // AddDrugByLotDialog (manualEntry), or the scanned lot was matched
      // straight to an existing inventory row with no stock
      // (matchedFromScan — see matchScannedLotToInventory), in which case the
      // read's scanId/timestamp are kept. Either way recordedManually marks it
      // for buildLotAllocations, which forwards the manual-entry fields to the
      // fulfillment mutation.
      addVialManually: (fields, { scanId = null, timestamp } = {}) =>
        dispatch({
          type: 'vial-scanned',
          parsed: { manualEntry: true, ...fields, recordedManually: true },
          scanId,
          timestamp: timestamp ?? new Date().toISOString(),
        }),
      setFulfillResult: (sessionId, status, error) =>
        dispatch({ type: 'fulfill-result', sessionId, status, error }),
      setLabelStatus: (sessionId, status) => dispatch({ type: 'label-status', sessionId, status }),
      setPrintStatus: (sessionId, status) => dispatch({ type: 'print-status', sessionId, status }),
      editRx: (patch) => dispatch({ type: 'edit-rx', patch }),
      editVial: (vialId, patch) => dispatch({ type: 'edit-vial', vialId, patch }),
      removeVial: (vialId) => dispatch({ type: 'remove-vial', vialId }),
      commitCurrent: () => dispatch({ type: 'commit-current', inventory }),
      discardCurrent: () => dispatch({ type: 'discard-current' }),
      dismissPendingVial: () => dispatch({ type: 'dismiss-pending-vial' }),
      promotePendingVial: () => dispatch({ type: 'promote-pending-vial' }),
      rollbackRxSwap: (scanId) => dispatch({ type: 'rollback-rx-swap', scanId }),
    }),
    [state, inventory]
  );

  return <ScanSessionContext.Provider value={value}>{children}</ScanSessionContext.Provider>;
};

ScanSessionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useScanSession = () => {
  const ctx = useContext(ScanSessionContext);
  if (!ctx) throw new Error('useScanSession must be used inside ScanSessionProvider');
  return ctx;
};

export const useScanSessionDispatch = useScanSession;

export default ScanSessionContext;
